import { type Kitchen, type SkuAnnotation, type SkuAnnotationData, type SkuAnnotations, SkuAnnotationType } from '@rta/rta-blueprint-library';

import { normalizeAnnotationFontSize } from '../../sku-annotations/helpers';
import type { AnnotationPreset } from '../../sku-annotations/types';

// Line keeps its second endpoint in x2/y2 and Doodle keeps its real extent only in `points` (relative to its own x/y), so both need their own branch -- width/height are 0 for them
export const getAnnotationLeftExtent = (a: Partial<SkuAnnotationData>): number => {
	if (a.type === SkuAnnotationType.Line) return Math.min(a.x ?? 0, a.x2 ?? 0);
	if (a.type === SkuAnnotationType.Doodle) return (a.x ?? 0) + Math.min(0, ...(a.points ?? []).map((p) => p.x));
	return a.x ?? 0;
};

export const getAnnotationRightExtent = (a: Partial<SkuAnnotationData>): number => {
	if (a.type === SkuAnnotationType.Line) return Math.max(a.x ?? 0, a.x2 ?? 0);
	if (a.type === SkuAnnotationType.Doodle) return (a.x ?? 0) + Math.max(0, ...(a.points ?? []).map((p) => p.x));
	return (a.x ?? 0) + (a.width ?? 0);
};

export const getAnnotationTopExtent = (a: Partial<SkuAnnotationData>): number => {
	if (a.type === SkuAnnotationType.Line) return Math.min(a.y ?? 0, a.y2 ?? 0);
	if (a.type === SkuAnnotationType.Doodle) return (a.y ?? 0) + Math.min(0, ...(a.points ?? []).map((p) => p.y));
	return a.y ?? 0;
};

export const getAnnotationBottomExtent = (a: Partial<SkuAnnotationData>): number => {
	if (a.type === SkuAnnotationType.Line) return Math.max(a.y ?? 0, a.y2 ?? 0);
	if (a.type === SkuAnnotationType.Doodle) return (a.y ?? 0) + Math.max(0, ...(a.points ?? []).map((p) => p.y));
	return (a.y ?? 0) + (a.height ?? 0);
};

/**
 * A preset is stored in blueprint-page units, but SKU overhead draws in kitchen inches, so a preset
 * crossing surfaces has to be rescaled or it lands ~10x off. The factor is the ratio of the two
 * surfaces' own default text box (120 page px wide against 12 inches).
 */
export const ANNOTATION_PRESET_UNITS_PER_SKU_UNIT = 10;

export type ScaleAnnotationRecordParams = {
	record: Partial<SkuAnnotationData>;
	scale: number;
	/** SKU-overhead font sizes still carry the legacy 8-72 encoding, so they normalize before they scale */
	normalizeFontSize: boolean;
};

export const scaleAnnotationRecord = (params: ScaleAnnotationRecordParams): Partial<SkuAnnotationData> => {
	const { record, scale, normalizeFontSize } = params;
	const { x = 0, y = 0, x2 = 0, y2 = 0, width = 0, height = 0, fontSize = 0, strokeWidth = 0, points, styleRuns } = record;
	const scaleFontSize = (value: number): number => (normalizeFontSize ? normalizeAnnotationFontSize(value) : value) * scale;

	return {
		...record,
		x: x * scale,
		y: y * scale,
		x2: x2 * scale,
		y2: y2 * scale,
		width: width * scale,
		height: height * scale,
		fontSize: scaleFontSize(fontSize),
		strokeWidth: strokeWidth * scale,
		points: points?.map((point) => ({ x: point.x * scale, y: point.y * scale })),
		styleRuns: styleRuns?.map((run) => (typeof run.fontSize === 'number' ? { ...run, fontSize: scaleFontSize(run.fontSize) } : run))
	};
};

export type ToAnnotationPresetRecordsParams = {
	members: Array<SkuAnnotation>;
	/** True when the members live in kitchen.skuAnnotations (inches) rather than on a blueprint page (page px) */
	isSkuSurface: boolean;
};

/**
 * The records a "save as preset" call persists: rebased onto the selection's own bounding-box origin
 * (measured from BOTH edges, so a right-to-left Line or a doodle with negative offsets rebases correctly)
 * and normalized into blueprint-page units. Shared by every save path so the two can never disagree with
 * each other or with `placeAnnotationPreset`.
 */
export const toAnnotationPresetRecords = (params: ToAnnotationPresetRecordsParams): Array<Record<string, unknown>> => {
	const { members, isSkuSurface } = params;
	const records = members.map((member): Partial<SkuAnnotationData> => (isSkuSurface
		? scaleAnnotationRecord({ record: member.serialize(), scale: ANNOTATION_PRESET_UNITS_PER_SKU_UNIT, normalizeFontSize: true })
		: member.serialize()));
	const originX = Math.min(...records.map(getAnnotationLeftExtent));
	const originY = Math.min(...records.map(getAnnotationTopExtent));

	return records.map((record): Record<string, unknown> => {
		const isLine = record.type === SkuAnnotationType.Line;
		return {
			...record,
			x: (record.x ?? 0) - originX,
			y: (record.y ?? 0) - originY,
			// Only a Line uses x2/y2; rebasing the zeroes every other type stores would bake in garbage negatives
			x2: isLine ? (record.x2 ?? 0) - originX : 0,
			y2: isLine ? (record.y2 ?? 0) - originY : 0
		};
	});
};

export type PlaceAnnotationPresetParams = {
	kitchen: Kitchen;
	preset: AnnotationPreset;
	pageNumber: number;
	centerX: number;
	centerY: number;
	// Defaults to the blueprint-page collection; SKU-overhead callers pass kitchen.skuAnnotations
	target?: SkuAnnotations | null;
};

/**
 * Instantiates a saved preset (#354) onto the given blueprint page, re-centered at (centerX, centerY)
 * -- "click a saved item in the stack to make it appear in the center of the viewport". A
 * multi-annotation preset is re-grouped under a fresh groupId (never the id it was saved with,
 * so placing the same preset twice never links the two copies together) and every member gets a
 * fresh id (never reuses the preset's stored id, which would collide with a prior placement).
 * Returns the new annotation ids so the caller can select them.
 */
export const placeAnnotationPreset = (params: PlaceAnnotationPresetParams): Array<string> => {
	const { kitchen, preset, pageNumber, centerX, centerY, target } = params;
	if (preset.annotations.length === 0) return [];

	const collection = target ?? kitchen.blueprintAnnotations;
	// Presets are stored in page units, so one placed on the SKU surface scales back down into kitchen inches
	const annotations = collection === kitchen.skuAnnotations
		? preset.annotations.map((record) => scaleAnnotationRecord({ record, scale: 1 / ANNOTATION_PRESET_UNITS_PER_SKU_UNIT, normalizeFontSize: false }))
		: preset.annotations;
	// Measured from both edges rather than assuming the saved origin is 0, so a preset saved with negative offsets (a right-to-left line) still centers on the click
	const minX = Math.min(...annotations.map(getAnnotationLeftExtent));
	const minY = Math.min(...annotations.map(getAnnotationTopExtent));
	const width = Math.max(...annotations.map(getAnnotationRightExtent)) - minX;
	const height = Math.max(...annotations.map(getAnnotationBottomExtent)) - minY;
	const originX = centerX - (width / 2) - minX;
	const originY = centerY - (height / 2) - minY;
	const groupId = annotations.length > 1 ? crypto.randomUUID() : '';

	return annotations.map((a) => {
		const { id: _id, createdBy: _createdBy, createDate: _createDate, groupId: _groupId, linkedIslandId: _linkedIslandId, ...rest } = a;
		const isLine = a.type === SkuAnnotationType.Line;
		const placed: Partial<SkuAnnotationData> = {
			...rest,
			x: (a.x ?? 0) + originX,
			y: (a.y ?? 0) + originY,
			// Only a Line uses x2/y2; every other type stores 0 there, and older presets rebased those zeroes into garbage negatives
			x2: isLine ? (a.x2 ?? 0) + originX : 0,
			y2: isLine ? (a.y2 ?? 0) + originY : 0,
			pageNumber,
			groupId
		};
		return collection.add(placed);
	});
};
